import { useMemo } from "react"
import { Ship } from "../../App/Ship"
import { GetApp } from "../../App"
import { GetShip, GetTrip } from "../../App/Context"
import { GetShipUsers } from "../ShipView"
import { Block, BlockField, BlockRow } from "../../Components/Block"
import { Icon, Icons } from "../../Components/Icon"
import { ILogAutoType, IShipStatus, IStatusType, Log } from "../../Types"
import { LogCommentText, LogNMEAComment, LogNMEAContext } from "../LogView"
import { coveringStatus, engineIdleAtLog, engineRunningAtLog, statusesCoveringLog } from "../Log/status"
import { PrintLogListViewProps } from "./Params"

export function PrintLogListView({ status, statuses, log, day, logs, auditLogEdits }: PrintLogListViewProps) {
  const app = GetApp()!;
  const [ship] = [GetShip()]; const ft = app.getT("LogNavigationView");
  const trip = GetTrip()?.trip;
  const shipUsers = GetShipUsers();

  const engineStatuses = useMemo(() => {
    return (statuses || []).filter((s) => s.statusType == IStatusType.engine);
  }, [statuses]);

  const engines = useMemo(() => {
    const engineInfo = [];
    const thresholds = Ship.shipSettings(ship).thresholds;
    for (const e of Ship.engines(ship)) {
      if (!e.number && !e.nmeaNumber) {
        continue;
      }
      const n = e.nmeaNumber || e.number || -1;
      const engineStatus = coveringStatus(engineStatuses, log.id, e.id);
      const on = engineRunningAtLog(log.engineRpms, n, engineStatus, thresholds);
      engineInfo.push({ id: e.id, number: e.number, nmeaNumber: e.nmeaNumber || e.number, name: e.engineShortName, on: on, idle: on && engineIdleAtLog(log.engineRpms, n, engineStatus, thresholds) });
    }
    return engineInfo;
  }, [engineStatuses, ship, log]);

  const sailStatuses = useMemo(() => {
    return statusesCoveringLog((statuses || []).filter((s) => s.statusType === IStatusType.sails), log.id);
  }, [statuses, log.id]);

  const hasSails = sailStatuses.length > 0;
  const context: LogNMEAContext = { app, ship, trip, shipUsers, logs, day, auditLogEdits };
  // Lighting entries need their generated description even in the compact layout.
  const comment = Log.parse(log).dataParsed.type == ILogAutoType.lighting
    ? LogCommentText(log, statuses || [], context)
    : LogNMEAComment(log, statuses || [], context);

  return (
    <Block className="PrintLogListView">
      <BlockRow className="print-log-list-row">
        <BlockField label={ft("status")} className="block-field-90 print-text-bold">
          <span>
            {status && (
              <>
                <Icon icon={`status-${IShipStatus[status.value]}`} /> {ft("status_" + IShipStatus[status.value])}
              </>
            )}
          </span>
        </BlockField>
        <BlockField label={ft("time")} className="block-field-45 print-text-bold">{app.format("time", log.time)}</BlockField>
        <BlockField label={ft("comment")} className="block-field-300">{comment}</BlockField>
        <BlockField label={ft("sails") + "|" + ft("engine")}className="block-field-75 text-centered">
          {Ship.isSailingVessel(ship) && hasSails && (
            <span>
              <Icon icon={Icons.SailingVesselLarge} />
              {" "}
            </span>
          )}
          {engines.map((engine, i) => {
            return (
              <span key={i}>
                {/* The shared engine rule, not the printed rpm: an engine started by hand
                    reports no rpm at all and still runs (#506), and one idling draws the
                    dot-in-ring the log rows use (#619, #1819). */}
                <Icon icon={!engine.on ? Icons.CircleLarge : (engine.idle ? Icons.CircleHalfLarge : Icons.CircleFilledLarge)} className="engine-icons" />
                {i < ship.engineCount - 1}
              </span>
            );
          })}
        </BlockField>

        <BlockField label={ft("course")} className="block-field-60">{app.format("course", log.course)}</BlockField>
        <BlockField label={ft("sog")} className="block-field-60">{app.format("speed", log.sog)}</BlockField>
        <BlockField label={ft("distance")} className="block-field-75">{app.format("distance", log.distance)}</BlockField>
      </BlockRow>

    </Block>
  );
}
